import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { strings } from '/imports/strings';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { randomString } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { DataCatalog } from '/imports/ui/DataCatalog';
import { createActiveFilter } from '/imports/ui/ActiveFilter';
import { Input } from '/imports/ui/Input';
import { InviteAccountModal } from '/imports/ui/InviteAccountModal';
import { Account, Accounts, Roles, listAccountsForOrg, updateAccount } from '/imports/api/accounts';

const AccountsPageStyled = styled(Page)`
  .heading-buttons { display: flex; gap: 0.5rem; }
`;

/** Lists the org's accounts with role filtering. */
export const AccountsPage = () => {
  const user = useUser();
  const orgId = user?.orgId;
  const [adminRoleFilter, setAdminRoleFilter] = useState(false);
  const [accounts, loading] = useMethod(listAccountsForOrg, orgId);
  const [catalogKey, setCatalogKey] = useState(randomString());
  const [inviting, setInviting] = useState(false);
  const ActiveFilter = useMemo(() => createActiveFilter<Account>(), []);
  // The Invite form offers only the roles the current user holds (a super user holds them all); the
  // server applies the same filter, so this is the honest set to show.
  const myAccount = useFindOne(() => Accounts.find({ orgId, userId: user?._id }), [`${orgId}_${user?._id}`]);
  const grantableRoles = user?.isSuperUser ? Object.values(Roles) : (myAccount?.roles || []);
  /** Keeps only admin accounts when the admin filter is active. */
  const _adminRoleFilter = (a: Account) => adminRoleFilter ? a.roles?.includes(Roles.admin) : true;
  return (
    <AccountsPageStyled className={`page${loading ? ' initializing' : ''}`} breadcrumbs={[{ path: '/accounts', label: strings.Accounts() }]}>
      <PageHeading
        left={<h1>{strings.Accounts()}</h1>}
        right={
          <div className="heading-buttons">
            <Button className="secondary" onClick={() => setInviting(true)}>{strings.Invite()}</Button>
            <Link to={'/accounts/new'}><Button>{strings.New()}</Button></Link>
          </div>
        }
      />
      {inviting && <InviteAccountModal grantableRoles={grantableRoles} onClose={() => setInviting(false)} />}
      <PageBody>
        <DataCatalog
          key={catalogKey}
          data={accounts || []}
          updateMethod={updateAccount}
          columns={[
            { field: '_user', label: strings.Username(), render: (document) => <Link to={`/accounts/${document._id}`}>{document._user?.username}</Link> },
            { field: 'firstName', input: { type: Input } }, { field: 'lastName', input: { type: Input } },
            { field: 'email' }, { field: 'roles', render: (document) => (document.roles || []).join(', ') }
          ]}
          tools={
            <div className="filters">
              <span className="filters-label">{strings.Filters()}:</span>
              <Button className={adminRoleFilter ? 'primary' : 'tertiary'} onClick={() => setAdminRoleFilter(!adminRoleFilter)}>{strings.Admin()}</Button>
              <ActiveFilter.Component onChange={() => setCatalogKey(randomString())} />
            </div>
          }
          filters={[ActiveFilter.filter(), _adminRoleFilter]}
          loading={loading}
        />
      </PageBody>
    </AccountsPageStyled>
  );
};